/**
 * Utilitarios de data com tratamento correto de timezone
 *
 * IMPORTANTE: Sempre use estas funcoes em vez de `new Date(string)` diretamente!
 *
 * O problema: `new Date("2025-01-31")` interpreta como meia-noite UTC,
 * que no Brasil (UTC-3) vira 21h do dia 30. Isso causa o bug de "dia anterior".
 *
 * A solucao: Estas funcoes parseiam datas como horario LOCAL, nao UTC.
 */

/**
 * Parseia uma string de data (YYYY-MM-DD ou ISO) como data LOCAL
 * Evita o problema de timezone onde "2025-01-31" vira dia 30 no Brasil
 *
 * @param dateStr - String de data (ex: "2025-01-31" ou "2025-01-31T10:30:00Z")
 * @returns Date object no timezone local, ou null se invalido
 */
export function parseDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null

  // Se for apenas data (YYYY-MM-DD), parseia como local
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  // Se tiver hora mas sem timezone (YYYY-MM-DDTHH:mm:ss), parseia como local
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(dateStr)) {
    const [datePart, timePart] = dateStr.split('T')
    const [year, month, day] = datePart.split('-').map(Number)
    const [hour, minute, second] = timePart.split(':').map(Number)
    return new Date(year, month - 1, day, hour, minute, second)
  }

  // Para ISO completo com timezone (ex: "2025-01-31T10:30:00Z" ou "2025-01-31T10:30:00.000Z")
  // Usa o parse nativo que respeita o timezone
  const date = new Date(dateStr)
  return isNaN(date.getTime()) ? null : date
}

/**
 * Parseia uma string de data, garantindo que retorna uma Date valida
 * Lanca erro se a data for invalida
 */
export function parseDateStrict(dateStr: string): Date {
  const date = parseDate(dateStr)
  if (!date) {
    throw new Error(`Data invalida: ${dateStr}`)
  }
  return date
}

/**
 * Formata uma data para o padrao brasileiro (DD/MM/YYYY)
 * Trata corretamente o timezone
 */
export function formatDateBR(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return '-'

  const date = typeof dateStr === 'string' ? parseDate(dateStr) : dateStr
  if (!date) return '-'

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/**
 * Formata uma data com hora para o padrao brasileiro (DD/MM/YYYY HH:mm)
 */
export function formatDateTimeBR(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return '-'

  const date = typeof dateStr === 'string' ? parseDate(dateStr) : dateStr
  if (!date) return '-'

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Formata uma data de forma relativa (ex: "Hoje", "Ontem", "15/01/2025")
 */
export function formatDateRelative(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return '-'

  const date = typeof dateStr === 'string' ? parseDate(dateStr) : dateStr
  if (!date) return '-'

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const dateOnly = new Date(date)
  dateOnly.setHours(0, 0, 0, 0)

  const diffDays = Math.floor((today.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Hoje'
  if (diffDays === 1) return 'Ontem'
  if (diffDays === -1) return 'Amanha'

  return formatDateBR(date)
}

/**
 * Calcula quantos dias se passaram desde uma data
 */
export function daysSince(dateStr: string | Date | null | undefined): number {
  if (!dateStr) return 0

  const date = typeof dateStr === 'string' ? parseDate(dateStr) : dateStr
  if (!date) return 0

  const now = new Date()
  return Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
}

/**
 * Calcula quantos dias faltam ate uma data
 */
export function daysUntil(dateStr: string | Date | null | undefined): number {
  if (!dateStr) return 0

  const date = typeof dateStr === 'string' ? parseDate(dateStr) : dateStr
  if (!date) return 0

  const now = new Date()
  now.setHours(0, 0, 0, 0)

  const targetDate = new Date(date)
  targetDate.setHours(0, 0, 0, 0)

  return Math.ceil((targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

/**
 * Verifica se uma data ja passou
 */
export function isExpired(dateStr: string | Date | null | undefined): boolean {
  if (!dateStr) return false

  const date = typeof dateStr === 'string' ? parseDate(dateStr) : dateStr
  if (!date) return false

  return date < new Date()
}

/**
 * Verifica se uma data esta proxima de expirar (dentro de N dias)
 */
export function isExpiringSoon(dateStr: string | Date | null | undefined, days: number = 3): boolean {
  if (!dateStr) return false

  const remaining = daysUntil(dateStr)
  return remaining >= 0 && remaining <= days
}

/**
 * Converte uma Date para string no formato YYYY-MM-DD (para inputs type="date")
 */
export function toDateInputValue(date: Date | null | undefined): string {
  if (!date) return ''

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}
